import { useState } from "react";
import * as React from "react";
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import {
  useGetFilterRuleVersion,
  useRulesVersion,
} from "@/hooks/useLocalStorage";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import {
  Edit,
  TreePine,
  List,
} from "lucide-react";
import {
  RuleVersion,
} from "../../shared/schema";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { useLocation } from "wouter";
import { Popup } from "@/widgets/RuleDetailDailog";
import { FilterTypeSearch } from "@/widgets/searchFilterType";
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { useBreadcrumbs } from "../BreadcrumbContext";

interface HierarchicalNodeProps {
  item: RuleVersion;
  level?: number;
}

interface TableHierarchicalNodeProps {
  topLevelTypesData: RuleVersion[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (code: number) => void;
}

function TableRuleVersion({
  topLevelTypesData,
  pageIndex,
  pageSize,
  totalCount,
  onPageChange,
}: TableHierarchicalNodeProps) {
  const [currentData, setCurrentData] = useState<RuleVersion[]>([]);
  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [reqStatus, setReqStatus] = React.useState(false);
  const [showPopup, setShowPopup] = React.useState(false);
  const [dataHead, setDataHead] = React.useState<RuleVersion | undefined>();
  const [dataContent, setDataContent] = React.useState<string | null>("");
  const [location, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [popupName, setPopupName] = React.useState("");

  React.useEffect(() => {
    if (topLevelTypesData) {
      setCurrentData(topLevelTypesData);
    }
  }, [topLevelTypesData]);

  function formatDate(dateString: string): string {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return "";
    }
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  const ruleTypeOptions = [
    { value: "0", label: "Eligibility Rules" },
    { value: "1", label: "Pricing Rules" },
    { value: "2", label: "Credit Rating Rules" },
    { value: "4", label: "Workflow Entry Conditions" },
    { value: "5", label: "Compliance Rules" },
    { value: "6", label: "Form Rules" },
  ];

  const getRuleTypeLabel = (typeValue: string) => {
    const option = ruleTypeOptions.find((opt) => opt.value === typeValue);
    return option ? option.label : "Unknown";
  };

  return (
    <div>
      <div className="border bg-white border-gray-200 rounded-lg p-2">
        <Table>
          <TableHeader>
            <TableRow>
              <>
                <TableHead>ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Raw Rule</TableHead>
                <TableHead>Rule Value</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Date Modified</TableHead>
                <TableHead>Actions</TableHead>
                <TableHead>Version</TableHead>
              </>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentData?.map((data) => (
              <TableRow key={data.Rule_Id}>
                <TableCell className="font-mono">{data.Rule_Id}</TableCell>

                <TableCell className="font-mono font-medium">
                  {data.Name}
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  {getRuleTypeLabel(String(data.Type))}
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  <Button
                    variant={"link"}
                    onClick={() => {
                      setPopupName("Raw Rule")
                      setDataHead(data);
                      setDataContent(data.raw_rule);
                      setShowPopup(true);
                    }}
                  >
                    View
                  </Button>
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  {data.Version ?
                    <Button
                      variant={"link"}
                      onClick={() => {
                        setPopupName("Raw with Value")
                        setDataHead(data);
                        setDataContent(data.rule_with_value);
                        setShowPopup(true);
                      }}
                    >
                      View
                    </Button>
                    : <span className="pl-4">New</span>
                  }
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  {data.Version}
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  {formatDate(data.DateTimeChanged)}
                </TableCell>

                <TableCell>
                  <div className="flex items-center space-x-2">
                    <Button
                      size="sm"
                      variant="link"
                      className="w-7 h-7 md:w-8 md:h-8 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        const encodedName = encodeURIComponent(data.Name);
                        queryClient.setQueryData(["selectedVersionRule"], data);
                        localStorage.setItem("selectedVersionRule", JSON.stringify(data));
                        setLocation(
                          `/version/edit/${data.Rule_Id}?name=${encodedName}`
                        );
                      }}
                      tooltip="Modify Rule Builder"
                    >
                      {data.Version ? "Modify" : "Create"}
                    </Button>
                  </div>
                </TableCell>

                <TableCell className="max-w-xs truncate">
                  <Button
                    variant={"link"}
                    onClick={() => {
                      const encodedName = encodeURIComponent(data.ObjectName);
                      setLocation(
                        `/ruleversion/${data.Rule_Id}?name=${encodedName}`
                      );
                    }}
                  >
                    Version History
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="mt-2">
          <PaginationControl
            pageIndex={pageIndex}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={onPageChange}
          />
        </div>
      </div>

      <AlertDialog
        open={alertOpen}
        onOpenChange={setAlertOpen}
        message={alertMessage}
        status={reqStatus}
      />

      {showPopup && (
        <Popup
          popupName={popupName}
          heading={dataHead}
          content={dataContent}
          onClose={() => setShowPopup(false)}
        />
      )}
    </div>
  );
}

function RuleVersionHierarchicalNode({ item, level = 0 }: HierarchicalNodeProps) {

  const [alertOpen, setAlertOpen] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState("");
  const [reqStatus, setReqStatus] = React.useState(false);

  const [showPopup, setShowPopup] = React.useState(false);
  const [dataHead, setDataHead] = React.useState<RuleVersion | undefined>();
  const [dataContent, setDataContent] = React.useState<string | null>("");
  const [popupName, setPopupName] = React.useState("");

  const [location, setLocation] = useLocation();


  return (
    <div className="border bg-white border-gray-200 rounded-lg">
      <div
        className="flex items-center space-x-2 md:space-x-3 p-2 md:p-3 hover:bg-gray-50 cursor-pointer"
        style={{ paddingLeft: `${8 + level * 16}px` }}
      >
        <div className="flex-1 min-w-0">
          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 ml-4">
              <span className="font-medium text-gray-900 truncate text-sm md:text-base">
                {(item as RuleVersion).Name || "TestRule"}
              </span>
              <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded flex-shrink-0">
                {(item as RuleVersion).Rule_Id}
              </span>
            </div>
          </div>
          {item.rule_with_value &&
            <Button
              variant={"link"}
              onClick={() => {
                setPopupName("Raw with Value")
                setDataHead(item);
                setDataContent(item.rule_with_value);
                setShowPopup(true);
              }}
            >
              View
            </Button>
          }
        </div>

        <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              const encodedName = encodeURIComponent(item.Name);
              setLocation(`/version/edit/${item.Rule_Id}?name=${encodedName}`);
            }}
            title="Modify"
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100"
          >
            <Edit size={20} strokeWidth={2.5} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              const encodedName = encodeURIComponent(item.ObjectName);
              setLocation(
                `/ruleversion/${item.Rule_Id}?name=${encodedName}`
              );
            }}
            title="Version History"
            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100"
          >
            <ManageHistoryIcon className="text-black" style={{ width: 20, height: 20 }} />
          </button>
        </div>

        <AlertDialog
          open={alertOpen}
          onOpenChange={setAlertOpen}
          message={alertMessage}
          status={reqStatus}
        />

        {showPopup && (
          <Popup
            popupName={popupName}
            heading={dataHead}
            content={dataContent}
            onClose={() => setShowPopup(false)}
          />
        )}
      </div>
    </div>
  );
}

export function RulesVersion() {
  const [viewMode, setViewMode] = useState<"table" | "tree">("table");
  const [topLevelTypes, setTopLevelTypes] = useState<RuleVersion[]>([]);

  const [typeInput, setTypeInput] = useState<string>("ALL");
  const [ruletype, setRuletype] = useState<number>(-2);
  const [typeFilter, setTypeFilter] = useState<string>("ALL");
  const [typeRule, setTypeRule] = useState<number>(-2);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterTypeData, setFilterTypeData] = useState<RuleVersion[]>([]);
  const [filterName, setFilterName] = useState<string>("");

  // pagination states
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(10);

  const { data: types, isLoading } = useRulesVersion(
    pageIndex,
    pageSize,
    typeFilter === "ALL" ? undefined : typeFilter,
    typeRule
  );

  const { setBreadcrumbs } = useBreadcrumbs();

  React.useEffect(() => {
    setBreadcrumbs([
      {
        label: `Rules Version`,
        path: `/ruleversion`,
      },
    ]);
  }, []);

  React.useEffect(() => {
    setTopLevelTypes(types?.items ?? []);
    setTotalCount(types?.total ?? 0);
  }, [types]);

  const { data: filterTypes, isFetching: isFilterFetching } = useGetFilterRuleVersion(
    filterName.length >= 3 ? filterName : undefined,
  );

  React.useEffect(() => {
    if (filterTypes && filterTypes.length > 0) {
      setFilterTypeData(filterTypes)
    } else {
      setFilterTypeData([])
    }
  }, [filterTypes])

  const ruleTypeOptions = [
    { value: "0", label: "Eligibility Rules" },
    { value: "1", label: "Pricing Rules" },
    { value: "2", label: "Credit Rating Rules" },
    { value: "4", label: "Workflow Entry Conditions" },
    { value: "5", label: "Compliance Rules" },
    { value: "6", label: "Form Rules" },
  ];

  // Check for navigation from search
  React.useEffect(() => {
    const selectedItemStr = localStorage.getItem("cm:selectedItem");
    if (selectedItemStr) {
      try {
        const selectedItem = JSON.parse(selectedItemStr);
        console.log("Navigate to item in TreeExplorer:", selectedItem);
        // Clear the selection after handling
        localStorage.removeItem("cm:selectedItem");

        // TODO: Expand tree to show the selected item
        // This could involve expanding parent nodes and highlighting the target
      } catch (error) {
        console.error("Error parsing selected item:", error);
        localStorage.removeItem("cm:selectedItem");
      }
    }
  }, []);

  const handleSearch = () => {
    setTypeFilter(typeInput);
    setTypeRule(ruletype)
  };

  const handleRefresh = (item: boolean) => {
    setTypeInput("ALL");
    setTypeFilter("ALL");
    setRuletype(-2);
    setTypeRule(-2);
    if (item) setFilterOpen(false);
  };

  return (
    <div className="min-h-full">
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
          <div className="min-w-0">
            <h2 className="text-lg md:text-xl font-semibold text-black">
              Rules Version
            </h2>
            <p className="text-black mt-1 text-sm md:text-base">
              Manage Rules and Version Setup
            </p>
          </div>

          <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <Button
                size="sm"
                variant={viewMode === "table" ? "default" : "ghost"}
                onClick={() => setViewMode("table")}
                className="h-8 text-xs md:text-sm flex-1 sm:flex-initial"
              >
                <List className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                <span className="hidden sm:inline">Table</span>
                <span className="sm:hidden">Table</span>
              </Button>
              <Button
                size="sm"
                variant={viewMode === "tree" ? "default" : "ghost"}
                onClick={() => setViewMode("tree")}
                className="h-8 text-xs md:text-sm flex-1 sm:flex-initial"
              >
                <TreePine className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                <span className="hidden sm:inline">Tree</span>
                <span className="sm:hidden">Tree</span>
              </Button>
            </div>
            <Button
              className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
              size="sm"
              onClick={() => setFilterOpen(true)}
            >
              Filter Rules Version
            </Button>
          </div>
        </div>
      </div>
      <div>
        {filterOpen &&
          <Card className="mb-4 md:mb-6">
            <CardContent className="pt-0">
              <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-3 md:space-y-0">
                <div className="w-[450px]">
                  <FilterTypeSearch
                    filterTypeData={filterTypeData}
                    isLoading={isFilterFetching}
                    value={typeInput}
                    onChange={setTypeInput}
                    onSearch={(query) => setFilterName(query)}
                  />
                </div>

                <div className="w-[450px]">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between text-sm"
                      >
                        {ruleTypeOptions.find((opt) => Number(opt.value) === ruletype)?.label ||
                          "Select Rule Type"}
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="start" className="w-[240px]">
                      <DropdownMenuRadioGroup
                        value={ruletype?.toString() ?? ""}
                        onValueChange={(val) =>
                          setRuletype(val ? Number(val) : -2)
                        }
                      >
                        <DropdownMenuRadioItem value="">
                          All Types
                        </DropdownMenuRadioItem>
                        {ruleTypeOptions.map((option) => (
                          <DropdownMenuRadioItem key={option.value} value={option.value}>
                            {option.label}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>


              <div className="mt-4 flex justify-center space-x-2">
                <Button onClick={handleSearch}>Search</Button>
                <Button variant="outline" onClick={() => handleRefresh(false)}>
                  Refresh
                </Button>
                <Button variant="outline" onClick={() => handleRefresh(true)}>
                  Close
                </Button>
              </div>

            </CardContent>
          </Card>
        }
      </div>

      {!isLoading ? (
        viewMode === "table" ? (
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="space-y-2">
                {topLevelTypes && topLevelTypes.length > 0 ? (
                  <>
                    <TableRuleVersion
                      topLevelTypesData={topLevelTypes}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      totalCount={totalCount}
                      onPageChange={setPageIndex}
                    />
                  </>
                ) : (
                  <div className="text-center py-8 text-black">
                    <p>No Version found. Create to get started.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-4 md:p-6">
              <div className="space-y-2">
                {topLevelTypes && topLevelTypes.length > 0 ? (
                  topLevelTypes.map((type) => (
                    <RuleVersionHierarchicalNode key={type.Rule_Id} item={type} />
                  ))
                ) : (
                  <div className="text-center py-8 text-black">
                    <p>No Rules found! Create to get started.</p>
                  </div>
                )}
                <PaginationControl
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={setPageIndex}
                />
              </div>
            </CardContent>
          </Card>
        )
      ) : (
        <div className="p-6 text-black">Loading...</div>
      )}
    </div>
  );
}
